import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { compose } from 'ramda';
import withField from 'js/components/common/Fields/withField';
import style from './SwissSelect.scss';

export const SelectBare = ({
    /* rff render props */
    input,
    meta = {},
    onChange,
    /* user provided props */
    id,
    name,
    label,
    hint,
    placeholder = '',
    className,
    classNames,
    options,
    disabled,
    readOnly,
    setValue,
    parse,
    onMouseDown,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    ...selectDomProps
}) => {
    const selectRef = useRef();
    const preventClick = useCallback(event => event.preventDefault(), []);
    const selectClassName = useMemo(() => [
        style.select,
        placeholder && (input.value == null || input.value === '') ? style.empty : ''
    ].filter(Boolean).join(' '), [placeholder, input.value]);
    const [isExpanded, setSelectExpandedState] = useState(false);
    const [selectValue, setSelectValue] = useState(options[0]);

    const handleClickAllExceptSelect = useCallback((event) => {
        if (isExpanded && event.target !== selectRef.current) {
            setSelectExpandedState(false);
        }
    }, [isExpanded]);

    const onClickSelect = useCallback((event) => {
        const newValue = options.filter(el => String(el.value) === event.target.getAttribute('value'))?.[0] || options[0];
        setSelectValue(newValue);
        input.onChange(event);
        setSelectExpandedState(false);
    }, [input, options]);

    const onInputClick = useCallback((event) => {
        event.preventDefault();
        setSelectExpandedState(true);
        event.stopPropagation();
    }, []);

    useEffect(() => {
        window.addEventListener('click', handleClickAllExceptSelect);

        return () => {
            window.removeEventListener('click', handleClickAllExceptSelect);
        };
    }, [handleClickAllExceptSelect, isExpanded]);

    return (
        <div className={`${style.fieldControl} swissSelect`} >
            <span className={`${style.floatingLabel} ${style.labelAbove}`}>{label}</span>
            <div className={style.selectContainer}>
                <input
                    {...input}
                    {...selectDomProps}
                    name={name}
                    className={style.inputSelect}
                    onClick={onInputClick}
                />
                <span className={`${style.dropdownArrow} ${isExpanded ? style.expandedArrow : style.collapsedArrow}`}></span>
                <div className={style.selectRibbon}></div>
                <div className={`${style.selectChosenWrapper} ${!isExpanded && style.selectChosenWrapperCollapsed}`}>{selectValue.label}</div>
                <ul
                    ref={selectRef}
                    id={selectValue.value}
                    value={selectValue.value}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    className={`${selectClassName} ${isExpanded ? style.expandedSelect : style.collapsedSelect}`}
                    {...meta.submitting ? { disabled: true } : {}}
                    onClick={onClickSelect}
                    onMouseDown={readOnly ? preventClick : onMouseDown}
                >
                    {(placeholder || isPlaceholderAsteriskEnabled) && (
                        <li disabled value="" className="hidden">
                            {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                        </li>
                    )}
                    {options.map(item => (
                        <li
                            key={item.value}
                            value={item.value}
                            className={classNames.option || ''}
                        >
                            {item.label}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

SelectBare.defaultProps = {
    options: [],
    className: '',
    classNames: {},
    placeholder: null
};

export const withFieldProps = Wrapped => props =>
    <Wrapped
        {...props}
    />;

const enhance = compose(
    withFieldProps,
    withField
);

export const SwissSelect = enhance(SelectBare);
